import { ENABLE_SHAKING, IS_SAFARI, SET_CELL_SHADOW } from "./config";
import { XLINKS, XMLNS } from "./constants";
import { addTouchEvent } from "./events";
import { GAME_MODE, gameEditorState, gameState } from "./game/gameState";
import store from "./store";

let i_ = -1;
export const CELL_STATE = Object.freeze({
  INDICATORS: i_++,
  OUTSIDE: i_++,
  FREE: i_++,
  BLOCKED: i_++,
  NON_BLOCKABLE: i_++,
  NON_BLOCKABLE_OUTSIDE: i_++,
  DROP: i_++,
  CENTER: i_++,
  SHUFFLE: i_++,
  CATCH: i_++,
  RANDOM: i_++,
  FREEZE: i_++,
  ERASE: i_++,
});

export const INVERSE_CELL_STATE = Object.freeze(
  Object.keys(CELL_STATE).reduce((acc, key) => {
    acc[CELL_STATE[key]] = key;
    return acc;
  }, {})
);

export const BOOSTER_CELLS_NAMES = Object.freeze({
  [CELL_STATE.DROP]: "Drop",
  [CELL_STATE.CENTER]: "Center",
  [CELL_STATE.SHUFFLE]: "Shuffle",
  [CELL_STATE.CATCH]: "Catch",
  [CELL_STATE.RANDOM]: "Random",
  [CELL_STATE.FREEZE]: "Freeze",
});

export const BOOSTER_CELLS_NAMES_EXTERNAL = Object.freeze({
  [CELL_STATE.DROP]: "Scatter Drop",
  [CELL_STATE.CENTER]: "Center Warp",
  [CELL_STATE.SHUFFLE]: "Chaos Shuffle",
  [CELL_STATE.CATCH]: "Instant Trap",
  [CELL_STATE.FREEZE]: "Frozen Hold",
  [CELL_STATE.RANDOM]: "Mystic Twist",
  [CELL_STATE.UNKNOWN]: "???",
});

// cell constructor
export function Cell(click, parentEl, state = CELL_STATE.OUTSIDE) {
  this.state = state;
  this.parentEl = parentEl;
  this.win = false;
  this.po = -1;
  this.id = null;
  this.touchHandle = function (e) {
    e.stopImmediatePropagation();
    click(this, this.cx, this.cy);
  };
}
export const cellToCoords = (i, j) => {
  const x = 2 * 34 + j * 34 + ((i % 2 ? 1 : -1) * 34) / 4;
  const y = 2 * 26 + i * 26;
  return { x, y };
};

// create SVG plot
Cell.prototype.createElement = function (
  i,
  j,
  editorMode,
  setShadow = SET_CELL_SHADOW
) {
  if (
    this.state == CELL_STATE.ERASE &&
    !(gameState.mode == GAME_MODE.EDITOR && gameEditorState.editing)
  )
    return;
  if (this.use && this.use.parentNode)
    this.use.parentNode.removeChild(this.use);
  this.editorMode = editorMode;
  const { x, y } = cellToCoords(i, j);
  const use = createCell(
    x,
    y,
    this.state,
    setShadow,
    false,
    this.editorMode,
    this.win
  );
  use.touchHandle = this.touchHandle;
  this.use = use;
  use.cx = j + 2;
  use.cy = i + 2;
  this.id = use;
  if (
    ![
      CELL_STATE.OUTSIDE,
      CELL_STATE.NON_BLOCKABLE,
      CELL_STATE.NON_BLOCKABLE_OUTSIDE,
    ].includes(this.state) ||
    gameState.mode == GAME_MODE.EDITOR
  ) {
    addTouchEvent(use, this.touchHandle);
    addTouchEvent(use, touchMoveHandle, true, "listener2", "touchmove");
  }
  if (this.parentEl) this.parentEl.appendChild(use);
};

let lastTouchedElement = null;
export function touchMoveHandle(event) {
  const touchLocation = event.touches[0];
  const touchedElement = document.elementFromPoint(
    touchLocation.clientX,
    touchLocation.clientY
  );

  if (touchedElement && touchedElement !== lastTouchedElement) {
    // Trigger event or call a function for the touched element
    if (touchedElement.touchHandle) touchedElement.touchHandle(event);
    lastTouchedElement = touchedElement;
  }
}

export function createCell(x, y, state, setShadow, disabled, editorMode, win) {
  const use = document.createElementNS(XMLNS, "use");
  use.setAttributeNS(null, "class", "cell");
  setUse(use, state, disabled, editorMode, win);
  use.setAttributeNS(null, "transform", `translate(${x},${y})`);
  if (setShadow && !IS_SAFARI)
    use.setAttributeNS(null, "filter", "url(#shadow)");
  return use;
}

export function setUse(use, state, disabled = false, editorMode, win) {
  const refId =
    editorMode || state == CELL_STATE.ERASE
      ? "editor-cell"
      : (state == CELL_STATE.CENTER
          ? "center-cell"
          : state == CELL_STATE.DROP
          ? "drop-cell"
          : state == CELL_STATE.SHUFFLE
          ? "shuffle-cell"
          : state == CELL_STATE.CATCH
          ? "catch-cell"
          : state == CELL_STATE.RANDOM
          ? "random-cell"
          : state == CELL_STATE.UNKNOWN
          ? "random-cell"
          : state == CELL_STATE.FREEZE
          ? "freeze-cell"
          : state == CELL_STATE.FREE
          ? win &&
            gameState.mode === GAME_MODE.EDITOR &&
            gameEditorState.editing &&
            gameEditorState.showLoosingFields
            ? "win-cell"
            : "free-cell"
          : state == CELL_STATE.BLOCKED
          ? "blocked-cell"
          : state == CELL_STATE.INDICATORS
          ? "indicators-cell"
          : state == CELL_STATE.OUTSIDE
          ? "outside-cell"
          : "non-blockable-cell") + (disabled ? "-disabled" : "");
  use.setAttributeNS(XLINKS, "xlink:href", `#${refId}`);
  if (refId == "win-cell") use.classList.add("win-cell");
}

export function createLeftCell(
  state,
  y_,
  scale_,
  scale = 1,
  setShadow = SET_CELL_SHADOW,
  disabled = false
) {
  const x =
    205 -
    ((275 / store.innerHeight) *
      (store.innerWidth -
        store.innerHeight *
          0.3 *
          (1 + (1 - scale) * 0.3) *
          (1 + (2.3 - scale) * 0.3) +
        (store.innerWidth * (1 - scale_) * 2) / 1.5)) /
      2;
  const y = 175 + y_ * 35;
  const use = createCell(x, y, state, setShadow, disabled);
  use.setAttribute(
    "transform",
    `${use.getAttribute("transform")} scale(${scale})`
  );
  use.setAttribute("id", `booster-${state}`);
  return use;
}

function resetClass(use, cls) {
  use.setAttributeNS(null, "class", cls);
}

export function shakeCell(use, small = false) {
  if (!ENABLE_SHAKING) return;
  const cls = use.getAttribute("class");
  use.setAttributeNS(null, "class", `${cls} shake${small ? "-s" : ""}`);
  setTimeout(resetClass, 400, use, cls);
}
